import React, { useContext, useState, useEffect } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Row, Col, Button, Modal, Form, Alert, Spinner, Accordion, Badge, Stack } from "react-bootstrap";
import axios from "axios";
import { FaEdit, FaTrash, FaPlus, FaStethoscope, FaFileAlt, FaCalendarAlt, FaFlask, FaMicroscope, FaCheckCircle, FaSpinner, FaUpload, FaTrashAlt, FaDownload, FaEye, FaFilePdf, FaFileImage, FaMoneyBillWave, FaTimes, FaLock } from "react-icons/fa";
import moment from "moment";
import { MdDateRange, MdAccessTime } from "react-icons/md";
import Select from "react-select";
import Swal from 'sweetalert2';
import { useNavigate } from "react-router-dom";

export default function PetLabTestComponent({ appointmentId, petId, isAppointmentOpen }) {
    const { t } = useContext(TranslatorContext);
    const [petLabTests, setPetLabTests] = useState([]);
    const [labTests, setLabTests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingLabTest, setEditingLabTest] = useState(null);
    const [selectedTests, setSelectedTests] = useState([]);
    const [recomendation, setRecomendation] = useState("");
    const [labTestDate, setLabTestDate] = useState("");
    const [generalNote, setGeneralNote] = useState("");
    const [existingNoteId, setExistingNoteId] = useState(null);
    const [openAccordion, setOpenAccordion] = useState(null);
    const [openNestedAccordion, setOpenNestedAccordion] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [isRecommended, setIsRecommended] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [showFileModal, setShowFileModal] = useState(false);
    const [selectedFileUrl, setSelectedFileUrl] = useState("");
    const [uploadingFileId, setUploadingFileId] = useState(null);
    const [hasLabTestForAppointment, setHasLabTestForAppointment] = useState(false);
    const navigate = useNavigate();

    // Get user role from localStorage
    const userRole = localStorage.getItem("role");
    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];

    const hasPermission = (perm) => {
        return permissions.includes(perm);
    };

    // Delete permission only for Admin
    const canDelete = () => {
        return userRole === 'Admin';
    };

    // File delete permission only for Admin
    const canDeleteFile = () => {
        return userRole === 'Admin';
    };

    // Edit permission for lab test
    const canEditLabTest = () => {
        return hasPermission("Appointment Lab Test edit");
    };

    // Helper function to format price without .00 if whole number
    const formatPrice = (price) => {
        if (price === null || price === undefined || price === "") return '';
        const num = parseFloat(price);
        if (isNaN(num)) return '';
        return num % 1 === 0 ? num.toString() : num.toFixed(2);
    };

    // Toast notification functions
    const showSuccessToast = (message) => {
        Swal.fire({
            icon: 'success',
            title: t('success'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };

    const showErrorToast = (message) => {
        Swal.fire({
            icon: 'error',
            title: t('error'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };

    const showWarningToast = (message) => {
        Swal.fire({
            icon: 'warning',
            title: t('warning'),
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };

    // Get status badge color
    const getStatusBadge = (status) => {
        switch (status) {
            case 'completed':
                return 'success';
            case 'processing':
                return 'warning';
            case 'pending':
                return 'info';
            case 'cancelled':
                return 'secondary';
            default:
                return 'secondary';
        }
    };

    useEffect(() => {
        fetchPetLabTests();
    }, [appointmentId, petId]);

    const fetchPetLabTests = async () => {
        try {
            setLoading(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/pet-lab-test/${petId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data) {
                const labTestsForPet = response.data.petLabTest || [];
                setPetLabTests(labTestsForPet);
                setLabTests(response.data.labTest || []);
                const hasLabTest = labTestsForPet.some(labTest => String(labTest?.appointment_id) === String(appointmentId));
                setHasLabTestForAppointment(hasLabTest);
                // Set current appointment accordion to open by default
                setOpenAccordion(String(appointmentId));
            }
        } catch (error) {
            console.error("Error fetching pet lab tests:", error);
            showErrorToast(t('failed_to_load_lab_tests'));
            setPetLabTests([]);
            setLabTests([]);
        } finally {
            setLoading(false);
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return null;
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return null;
            return {
                date: date.toLocaleDateString(),
                time: date.toLocaleTimeString(),
                full: date.toLocaleString()
            };
        } catch (error) {
            return null;
        }
    };

    const handleStatusChange = async (labTestId, currentStatus) => {
        if (!canEditLabTest()) {
            showWarningToast(t('you_dont_have_permission_to_edit'));
            return;
        }

        try {
            const AuthToken = localStorage.getItem("authToken");
            const newStatus = currentStatus === 'completed' ? 'processing' : 'completed';
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/pet-lab-test-update/${labTestId}`,
                { status: newStatus },
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data) {
                fetchPetLabTests();
                showSuccessToast(t('Status changed successfully!'));
            }
        } catch (error) {
            console.error("Error changing status:", error);
            showErrorToast(t('failed_to_change_status'));
        }
    };

    const handleAddResult = (labTestId) => {
        if (!canEditLabTest()) {
            showWarningToast(t('you_dont_have_permission_to_edit'));
            return;
        }
        navigate(`/pet-lab-test-result/${labTestId}`);
    };

    const handleViewReport = (labTestId) => {
        window.open(`${process.env.REACT_APP_URL}/lab-report/${labTestId}`, '_blank');
    };

    const handleTestSelect = (selectedOptions) => {
        const testsList = selectedOptions.map(opt => ({
            lab_test_id: opt.value,
            price: opt.price !== undefined && opt.price !== null ? opt.price.toString() : ""
        }));
        setSelectedTests(testsList);
    };

    const handleTestPriceChange = (testId, newPrice) => {
        const updatedTests = selectedTests.map(test =>
            test.lab_test_id === testId
                ? { ...test, price: newPrice }
                : test
        );
        setSelectedTests(updatedTests);
    };

    const handleRemoveTest = (testId) => {
        const updatedTests = selectedTests.filter(test => test.lab_test_id !== testId);
        setSelectedTests(updatedTests);
    };

    const handleFileUpload = async (labTestId, file) => {
        if (!canEditLabTest()) {
            showWarningToast(t('you_dont_have_permission_to_upload_file'));
            return;
        }

        if (!file) {
            showWarningToast(t('please_select_file'));
            return;
        }

        setUploading(true);
        setUploadingFileId(labTestId);
        try {
            const AuthToken = localStorage.getItem("authToken");
            const formData = new FormData();
            formData.append('test_file', file);

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/pet-lab-test-upload-file/${labTestId}`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${AuthToken}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            if (response.data) {
                showSuccessToast(t('file_uploaded_successfully'));
                fetchPetLabTests();
            }
        } catch (error) {
            console.error("Error uploading file:", error);
            showErrorToast(t('failed_to_upload_file'));
        } finally {
            setUploading(false);
            setUploadingFileId(null);
        }
    };

    const handleDeleteFile = async (labTestId) => {
        if (!canDeleteFile()) {
            showWarningToast(t('only_admin_can_delete_files'));
            return;
        }

        Swal.fire({
            title: t('Are you sure?'),
            text: t("Delete this file?"),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('Yes, delete it!'),
            cancelButtonText: t('Cancel'),
            preConfirm: async () => {
                try {
                    const AuthToken = localStorage.getItem("authToken");
                    const response = await axios.post(
                        `${process.env.REACT_APP_API_URL}/pet-lab-test-delete-file/${labTestId}`,
                        {},
                        { headers: { Authorization: `Bearer ${AuthToken}` } }
                    );

                    if (response.data) {
                        fetchPetLabTests();
                        showSuccessToast(t('file_deleted_successfully'));
                        return true;
                    }
                } catch (error) {
                    console.error("Error deleting file:", error);
                    showErrorToast(t('failed_to_delete_file'));
                    return false;
                }
            }
        });
    };

    const handleViewFile = (fileUrl) => {
        const fullUrl = `${process.env.REACT_APP_IMG_URL}/${fileUrl}`;
        setSelectedFileUrl(fullUrl);
        setShowFileModal(true);
    };

    const handleDownloadFile = (fileUrl, fileName) => {
        const fullUrl = `${process.env.REACT_APP_IMG_URL}/${fileUrl}`;
        window.open(fullUrl, '_blank');
    };

    const handleAddLabTest = () => {
        if (!canEditLabTest()) {
            showWarningToast(t('you_dont_have_permission_to_add'));
            return;
        }
        setEditingLabTest(null);
        setSelectedTests([]);
        setRecomendation("");
        setLabTestDate("");
        setGeneralNote("");
        setExistingNoteId(null);
        setIsRecommended(false);
        setSelectedFile(null);
        setShowModal(true);
    };

    const handleEditLabTest = (labTest) => {
        if (!canEditLabTest()) {
            showWarningToast(t('you_dont_have_permission_to_edit'));
            return;
        }
        setEditingLabTest(labTest);
        setSelectedTests([{
            lab_test_id: labTest.lab_test_id,
            price: labTest.price !== null && labTest.price !== undefined ? labTest.price.toString() : ""
        }]);
        setRecomendation(labTest.recomendation || "");
        setLabTestDate(labTest.date || "");

        setIsRecommended(!!(labTest.recomendation && labTest.recomendation.trim()));

        if (labTest.note && Array.isArray(labTest.note) && labTest.note.length > 0) {
            setGeneralNote(labTest.note[0].note || "");
            setExistingNoteId(labTest.note[0].id);
        } else {
            setGeneralNote("");
            setExistingNoteId(null);
        }

        setSelectedFile(null);
        setShowModal(true);
    };

    const handleDeleteLabTest = async (labTestId) => {
        if (!canDelete()) {
            showWarningToast(t('only_admin_can_delete'));
            return;
        }

        try {
            setSubmitting(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.delete(
                `${process.env.REACT_APP_API_URL}/pet-lab-test-delete/${labTestId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data) {
                fetchPetLabTests();
                showSuccessToast(t('Lab test deleted successfully!'));
            }
        } catch (error) {
            console.error("Error deleting lab test:", error);
            showErrorToast(t('failed_to_delete_lab_test'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (labTestId, labTestName) => {
        Swal.fire({
            title: t('Are you sure?'),
            text: t("You won't be able to revert this!"),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('Yes, delete it!'),
            cancelButtonText: t('Cancel'),
        }).then((result) => {
            if (result.isConfirmed) {
                handleDeleteLabTest(labTestId);
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isRecommended && selectedTests.length === 0) {
            showWarningToast(t('please_select_at_least_one_test'));
            return;
        }

        if (!isRecommended && !labTestDate) {
            showWarningToast(t('please_select_lab_test_date'));
            return;
        }

        // Process prices: convert empty strings to 0
        const processedTests = selectedTests.map(test => ({
            lab_test_id: test.lab_test_id,
            price: test.price === "" || test.price === null || test.price === undefined ? 0 : parseFloat(test.price) || 0
        }));

        setSubmitting(true);

        try {
            const AuthToken = localStorage.getItem("authToken");
            const url = editingLabTest
                ? `${process.env.REACT_APP_API_URL}/pet-lab-test-update/${editingLabTest.id}`
                : `${process.env.REACT_APP_API_URL}/pet-lab-test`;

            let payload;

            if (editingLabTest) {
                payload = {
                    appointment_id: appointmentId,
                    lab_test_id: !isRecommended ? processedTests[0]?.lab_test_id : null,
                    price: !isRecommended ? processedTests[0]?.price : null,
                    recomendation: isRecommended ? (recomendation || null) : null,
                    date: labTestDate || null,
                    note: generalNote || null,
                };
                if (existingNoteId) {
                    payload.note_id = existingNoteId;
                }
            } else {
                payload = {
                    appointment_id: appointmentId,
                    tests: isRecommended ? [] : processedTests.map(test => ({
                        lab_test_id: test.lab_test_id,
                        price: test.price
                    })),
                    recomendation: isRecommended ? (recomendation || null) : null,
                    date: labTestDate || null,
                    note: generalNote || null,
                };
            }

            const response = await axios({
                method: 'post',
                url: url,
                data: payload,
                headers: {
                    Authorization: `Bearer ${AuthToken}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.data.success || response.data) {
                setShowModal(false);
                fetchPetLabTests();
                showSuccessToast(editingLabTest ? t('Lab test updated successfully!') : t('Lab test added successfully!'));
            }
        } catch (error) {
            console.error("Error saving lab test:", error);
            showErrorToast(error.response?.data?.message || t('failed_to_save_lab_test'));
        } finally {
            setSubmitting(false);
        }
    };

    const getLabTestOptions = () => {
        const options = [];
        labTests.forEach(parent => {
            options.push({
                value: null,
                label: parent.name,
                isDisabled: true,
                isParent: true
            });

            if (parent.child_lab_tests && parent.child_lab_tests.length > 0) {
                parent.child_lab_tests.forEach(child => {
                    options.push({
                        value: child.id,
                        label: `   └ ${child.name} - ${formatPrice(child.price || 0)}`,
                        price: child.price
                    });
                });
            }
        });
        return options;
    };

    const customSelectStyles = {
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.data.isDisabled ? '#f0f0f0' : state.isFocused ? '#e0e0e0' : 'white',
            color: state.data.isDisabled ? '#999' : '#333',
            fontStyle: state.data.isDisabled ? 'italic' : 'normal',
            fontWeight: state.data.isDisabled ? 'bold' : 'normal',
            cursor: state.data.isDisabled ? 'not-allowed' : 'pointer',
            padding: '10px',
        }),
        control: (provided) => ({
            ...provided,
            borderColor: '#ced4da',
            '&:hover': {
                borderColor: '#86b7fe'
            }
        })
    };

    const isImageFile = (filename) => {
        return /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(filename);
    };

    const getThumbnailUrl = (fileUrl) => {
        return `${process.env.REACT_APP_IMG_URL}/${fileUrl}`;
    };

    const labTestOptions = getLabTestOptions();
    const selectedOptions = selectedTests.map(test => {
        let testName = 'Unknown';
        for (const parent of labTests) {
            const child = parent.child_lab_tests?.find(c => c.id === test.lab_test_id);
            if (child) {
                testName = child.name;
                break;
            }
        }
        return {
            value: test.lab_test_id,
            label: testName,
            price: test.price
        };
    }).filter(opt => opt.value);

    const totalAmount = selectedTests.reduce((sum, test) => {
        const price = test.price === "" || test.price === null || test.price === undefined ? 0 : parseFloat(test.price) || 0;
        return sum + price;
    }, 0);

    const groupLabTestsByAppointment = () => {
        const grouped = {};
        petLabTests.forEach(labTest => {
            const appointmentIdKey = labTest.appointment_id;
            if (!grouped[appointmentIdKey]) {
                grouped[appointmentIdKey] = {
                    appointment: labTest.appointment,
                    labTests: []
                };
            }
            grouped[appointmentIdKey].labTests.push(labTest);
        });

        const sortedEntries = Object.entries(grouped).sort((a, b) => {
            const aId = String(a[0]);
            const bId = String(b[0]);
            const currentId = String(appointmentId);

            if (aId === currentId) return -1;
            if (bId === currentId) return 1;
            return 0;
        });

        return Object.fromEntries(sortedEntries);
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 mb-0">{t('loading lab tests')}</p>
            </div>
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom flex-wrap gap-2">
                <h6 className="mb-0 text-muted">{t('lab tests list')}</h6>
                {isAppointmentOpen && !hasLabTestForAppointment &&
                    hasPermission("Appointment Lab Test Create") && canEditLabTest() && (
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={handleAddLabTest}
                            className="d-flex align-items-center gap-2"
                        >
                            <FaPlus size={12} /> {t('add lab test')}
                        </Button>
                    )}
            </div>

            {!petLabTests || petLabTests.length === 0 ? (
                <Alert variant="info" className="text-center py-4">
                    <FaFlask size={30} className="mb-2 text-muted" />
                    <p className="mb-0">{t('no lab tests found')}</p>
                </Alert>
            ) : (
                <Accordion
                    activeKey={openAccordion}
                    onSelect={(key) => setOpenAccordion(key)}
                    className="appointments-accordion"
                >
                    {Object.entries(groupLabTestsByAppointment()).map(([appointmentIdKey, appointmentData]) => {
                        const { appointment, labTests: appointmentLabTests } = appointmentData;
                        const isCurrentAppointment = String(appointmentIdKey) === String(appointmentId);

                        return (
                            <Accordion.Item eventKey={appointmentIdKey} key={appointmentIdKey} className="mb-3 border rounded">
                                <Accordion.Header className="bg-light">
                                    <div className="d-flex justify-content-between align-items-center w-100 me-3 pe-3">
                                        <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 gap-md-3">
                                            <div className="text-muted small">
                                                <strong className="text-primary">{appointment?.appointment_number || 'Unknown Appointment'}</strong>
                                            </div>
                                            <div className="text-muted small">
                                                👨‍⚕️ <strong>Doctor:</strong> {appointment?.doctor?.name || 'Unknown Doctor'}
                                            </div>
                                            <Badge bg="info" className="px-3 py-2">
                                                {appointmentLabTests.length} {t('test(s)')}
                                            </Badge>
                                        </div>
                                    </div>
                                </Accordion.Header>
                                <Accordion.Body className="bg-white p-0">
                                    <Accordion
                                        activeKey={openNestedAccordion[appointmentIdKey]}
                                        onSelect={(key) => setOpenNestedAccordion({
                                            ...openNestedAccordion,
                                            [appointmentIdKey]: key
                                        })}
                                        className="nested-lab-tests-accordion pe-3 ps-3 pt-3"
                                    >
                                        {appointmentLabTests.map((labTest) => {
                                            if (!labTest) return null;
                                            const dateTime = formatDateTime(labTest.date);
                                            const noteText = labTest.note && Array.isArray(labTest.note) && labTest.note.length > 0
                                                ? labTest.note[0].note
                                                : null;

                                            return (
                                                <Accordion.Item eventKey={String(labTest.id)} key={labTest.id} className="mb-3 border rounded">
                                                    <Accordion.Header className="bg-light">
                                                        <div className="d-flex justify-content-between align-items-center w-100 me-3 pe-3 flex-wrap gap-2">
                                                            <div className="d-flex flex-column flex-md-row align-items-md-center gap-2 gap-md-3 flex-wrap">
                                                                <FaStethoscope className="text-primary" />
                                                                {!labTest.recomendation && (
                                                                    <Badge bg="success" className="px-3 py-2">
                                                                        {labTest.lab_test?.name || 'N/A'}
                                                                    </Badge>
                                                                )}
                                                                {labTest.recomendation && (
                                                                    <Badge bg="warning" className="px-3 py-2">
                                                                        {t('Recommended')}
                                                                    </Badge>
                                                                )}

                                                                {dateTime && !labTest.recomendation && (
                                                                    <div className="d-flex align-items-center gap-3 text-muted small">
                                                                        <span className="d-flex align-items-center gap-1">
                                                                            <MdDateRange size={14} />
                                                                            {moment(dateTime.date).format("DD MMM YYYY")}
                                                                        </span>
                                                                        <span className="d-flex align-items-center gap-1">
                                                                            <MdAccessTime size={14} />
                                                                            {moment(dateTime.time, "HH:mm:ss").format("hh:mm A")}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                {/* Status Badge */}
                                                                {!labTest.recomendation && labTest.status && (
                                                                    <Badge bg={getStatusBadge(labTest.status)} className="px-3 py-2">
                                                                        <FaSpinner className="me-1" size={10} />
                                                                        {labTest.status}
                                                                    </Badge>
                                                                )}
                                                            </div>
                                                            <div className="d-flex gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
                                                                {!labTest.recomendation && labTest.status !== 'completed' && canEditLabTest() && (
                                                                    <Button
                                                                        variant="success"
                                                                        size="sm"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleAddResult(labTest.id);
                                                                        }}
                                                                        className="d-flex align-items-center gap-1"
                                                                        title={t('add_result')}
                                                                    >
                                                                        <FaFlask size={12} />
                                                                        <span className="d-none d-sm-inline">{t('add result')}</span>
                                                                    </Button>
                                                                )}

                                                                {/* View Report Button - Show when status is completed */}
                                                                {!labTest.recomendation && labTest.status === 'completed' && (
                                                                    <Button
                                                                        variant="info"
                                                                        size="sm"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleViewReport(labTest.id);
                                                                        }}
                                                                        className="d-flex align-items-center gap-1"
                                                                        title={t('view_report')}
                                                                    >
                                                                        <FaEye size={12} />
                                                                        <span className="d-none d-sm-inline">{t('view_report')}</span>
                                                                    </Button>
                                                                )}
                                                                {canEditLabTest() && (
                                                                    <Button
                                                                        variant="outline-primary"
                                                                        size="sm"
                                                                        onClick={() => handleEditLabTest(labTest)}
                                                                        className="d-flex align-items-center gap-1"
                                                                        style={{ background: '#3182ce', color: '#fff' }}
                                                                    >
                                                                        <FaEdit size={12} />
                                                                        <span className="d-none d-sm-inline">{t('edit')}</span>
                                                                    </Button>
                                                                )}
                                                                {canDelete() && (
                                                                    <Button
                                                                        variant="danger"
                                                                        size="sm"
                                                                        onClick={() => handleDeleteClick(labTest.id, labTest.lab_test?.name)}
                                                                        className="d-flex align-items-center gap-1"
                                                                    >
                                                                        <FaTrash size={12} />
                                                                        <span className="d-none d-sm-inline">{t('delete')}</span>
                                                                    </Button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </Accordion.Header>
                                                    <Accordion.Body className="bg-white">
                                                        <Row className="g-4">
                                                            <Col xs={12}>
                                                                
                                                                <div className="p-3 bg-light rounded">
                                                                    {!labTest.recomendation ? (
                                                                        <>
                                                                            <div className="mb-2">
                                                                                <strong className="text-muted">{t('Lab Test Name')}:</strong> {labTest.lab_test?.name || 'N/A'}
                                                                            </div>
                                                                            {labTest.lab_test?.description && (
                                                                                <div className="mb-2">
                                                                                    <strong className="text-muted">{t('Description')}:</strong> {labTest.lab_test?.description}
                                                                                </div>
                                                                            )}
                                                                            {labTest.date && (
                                                                                <div className="mb-2">
                                                                                    <strong className="text-muted">{t('Lab Test Date')}:</strong>{' '}
                                                                                    {moment(labTest.date).format("DD MMM YYYY hh:mm A")}
                                                                                </div>
                                                                            )}
                                                                            {/* Status in Details */}
                                                                            {labTest.status && (
                                                                                <div className="mb-2">
                                                                                    <strong className="text-muted">{t('Status')}:</strong>{' '}
                                                                                    <Badge bg={getStatusBadge(labTest.status)}>
                                                                                        {labTest.status}
                                                                                    </Badge>
                                                                                </div>
                                                                            )}
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            {labTest.recomendation && (
                                                                                <div className="mb-2">
                                                                                    <strong className="text-muted">{t('Recommendation')}:</strong> {labTest.recomendation}
                                                                                </div>
                                                                            )}
                                                                            {labTest.date && (
                                                                                <div className="mb-2">
                                                                                    <strong className="text-muted">{t('Date')}:</strong>{' '}
                                                                                    {new Date(labTest.date).toLocaleString()}
                                                                                </div>
                                                                            )}
                                                                        </>
                                                                    )}

                                                                    {/* File Upload Section */}
                                                                    <div className="mt-3 pt-2 border-top">
                                                                        <div className="d-flex align-items-center justify-content-between gap-2 mb-2 flex-wrap">
                                                                            <div className="d-flex align-items-center gap-2">
                                                                                <FaUpload className="text-info" />
                                                                                <strong className="text-muted">{t('Test File')}:</strong>
                                                                            </div>
                                                                            {labTest.test_file && (
                                                                                <div className="d-flex gap-2 flex-wrap">
                                                                                    <Button
                                                                                        variant="info"
                                                                                        size="sm"
                                                                                        onClick={() => handleViewFile(labTest.test_file)}
                                                                                    >
                                                                                        <FaEye size={12} /> {t('view')}
                                                                                    </Button>
                                                                                    <Button
                                                                                        variant="success"
                                                                                        size="sm"
                                                                                        onClick={() => handleDownloadFile(labTest.test_file, labTest.lab_test?.name || 'lab-test')}
                                                                                    >
                                                                                        <FaDownload size={12} /> {t('download')}
                                                                                    </Button>
                                                                                    {canDeleteFile() && (
                                                                                        <Button
                                                                                            variant="danger"
                                                                                            size="sm"
                                                                                            onClick={() => handleDeleteFile(labTest.id)}
                                                                                        >
                                                                                            <FaTrashAlt size={12} /> {t('delete')}
                                                                                        </Button>
                                                                                    )}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                        {labTest.test_file && (
                                                                            <div
                                                                                className="mb-2 file-thumbnail"
                                                                                style={{ cursor: 'pointer' }}
                                                                                onClick={() => handleViewFile(labTest.test_file)}
                                                                            >
                                                                                {isImageFile(labTest.test_file) ? (
                                                                                    <img
                                                                                        src={getThumbnailUrl(labTest.test_file)}
                                                                                        alt={labTest.test_file.split('/').pop()}
                                                                                        className="img-fluid"
                                                                                        style={{
                                                                                            maxWidth: '100%',
                                                                                            maxHeight: '150px',
                                                                                            objectFit: 'contain',
                                                                                            border: '1px solid #ddd',
                                                                                            borderRadius: '4px',
                                                                                            padding: '5px'
                                                                                        }}
                                                                                        onError={(e) => {
                                                                                            e.target.style.display = 'none';
                                                                                            e.target.nextSibling.style.display = 'flex';
                                                                                        }}
                                                                                    />
                                                                                ) : (
                                                                                    <div
                                                                                        className="d-flex align-items-center gap-2 p-2 border rounded bg-light"
                                                                                    >
                                                                                        {labTest.test_file.toLowerCase().includes('.pdf') ? (
                                                                                            <FaFilePdf size={40} color="red" />
                                                                                        ) : (
                                                                                            <FaFileImage size={40} color="#6c757d" />
                                                                                        )}
                                                                                        <span className="text-primary small">
                                                                                            {labTest.test_file.split('/').pop()}
                                                                                        </span>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                        {!labTest.test_file && canEditLabTest() && (
                                                                            <div className="mt-2">
                                                                                <Form.Group>
                                                                                    <Form.Control
                                                                                        type="file"
                                                                                        size="sm"
                                                                                        onChange={(e) => {
                                                                                            const file = e.target.files[0];
                                                                                            if (file) {
                                                                                                handleFileUpload(labTest.id, file);
                                                                                            }
                                                                                        }}
                                                                                        accept=".pdf,.jpg,.jpeg,.png"
                                                                                        disabled={uploading && uploadingFileId === labTest.id}
                                                                                    />
                                                                                    {uploading && uploadingFileId === labTest.id && (
                                                                                        <Spinner animation="border" size="sm" className="mt-2" />
                                                                                    )}
                                                                                </Form.Group>
                                                                            </div>
                                                                        )}
                                                                        {!labTest.test_file && !canEditLabTest() && (
                                                                            <div className="mt-2 text-muted">
                                                                                <FaLock className="me-1" /> {t('No file uploaded')}
                                                                            </div>
                                                                        )}
                                                                    </div>

                                                                    {noteText && (
                                                                        <div className="mt-3 pt-2 border-top">
                                                                            <div className="d-flex align-items-center gap-2">
                                                                                <FaFileAlt className="text-success" />
                                                                                <strong className="text-muted">{t('note')}:</strong>
                                                                            </div>
                                                                            <p className="mb-0 mt-1 text-secondary">{noteText}</p>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            );
                                        })}
                                    </Accordion>
                                </Accordion.Body>
                            </Accordion.Item>
                        );
                    })}
                </Accordion>
            )}

            {/* Add/Edit Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
                <Modal.Header closeButton className="custom-modal-header">
                    <Modal.Title className="custom-modal-title">
                        {editingLabTest ? t('Edit Lab Test') : t('Add Lab Tests')}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body className="custom-modal-body">
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                id="recommended-checkbox"
                                label={<span className="fw-bold">{t('Recommended')}</span>}
                                checked={isRecommended}
                                onChange={(e) => setIsRecommended(e.target.checked)}
                            />
                        </Form.Group>

                        {!isRecommended ? (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">
                                        {t('Select Lab Tests')} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Select
                                        options={labTestOptions}
                                        value={selectedOptions}
                                        onChange={handleTestSelect}
                                        placeholder={t('select lab tests')}
                                        isMulti
                                        isSearchable
                                        styles={customSelectStyles}
                                        formatOptionLabel={(option) => (
                                            <span style={{
                                                marginLeft: option.isParent ? '0' : '20px',
                                                fontWeight: option.isParent ? 'bold' : 'normal',
                                                color: option.isParent ? '#666' : '#333'
                                            }}>
                                                {option.label}
                                            </span>
                                        )}
                                    />
                                    <small className="text-muted d-block mt-1">
                                        {t('You can select multiple lab tests')}
                                    </small>
                                </Form.Group>

                                {selectedTests.length > 0 && (
                                    <div className="mb-3">
                                        <Form.Label className="fw-bold d-flex align-items-center gap-2">
                                            <FaMoneyBillWave className="text-success" /> {t('Prices')}
                                        </Form.Label>
                                        {selectedTests.map((test, index) => {
                                            let testInfo = null;
                                            for (const parent of labTests) {
                                                const child = parent.child_lab_tests?.find(c => c.id === test.lab_test_id);
                                                if (child) {
                                                    testInfo = child;
                                                    break;
                                                }
                                            }
                                            return (
                                                <div key={index} className="mb-2 p-2 border rounded bg-light">
                                                    <Row className="align-items-center g-2">
                                                        <Col xs={12} sm={6}>
                                                            <strong>{testInfo?.name || 'Unknown Test'}</strong>
                                                        </Col>
                                                        <Col xs={8} sm={4}>
                                                            <Form.Control
                                                                type="number"
                                                                step="0.01"
                                                                min="0"
                                                                value={test.price}
                                                                onFocus={(e) => e.target.select()}
                                                                onChange={(e) => handleTestPriceChange(test.lab_test_id, e.target.value)}
                                                                placeholder={t('Enter price')}
                                                            />
                                                        </Col>
                                                        <Col xs={2} sm={1} className="text-end">
                                                            <Button
                                                                variant="danger"
                                                                size="sm"
                                                                onClick={() => handleRemoveTest(test.lab_test_id)}
                                                                className="d-flex align-items-center justify-content-center w-100"
                                                            >
                                                                <FaTimes />
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                    <div className="mt-1">
                                                        <small className="text-muted">
                                                            {t('Default price')}: {formatPrice(testInfo?.price || 0)}
                                                        </small>
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        <div className="rounded text-end">
                                            <strong>{t('Total Amount')}: </strong>
                                            <span style={{ fontSize: '18px', fontWeight: 'bold', color: 'black' }}>
                                                {formatPrice(totalAmount)}
                                            </span>
                                        </div>
                                    </div>
                                )}

                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold d-flex align-items-center gap-2">
                                        <FaCalendarAlt /> {t('Lab Test Date')} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="datetime-local"
                                        value={labTestDate}
                                        onChange={(e) => setLabTestDate(e.target.value)}
                                        required
                                    />
                                </Form.Group>
                            </>
                        ) : (
                            <>
                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold">
                                        {t('Recommendations')}
                                    </Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        value={recomendation}
                                        onChange={(e) => setRecomendation(e.target.value)}
                                        placeholder={t('Enter recommendations here...')}
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label className="fw-bold d-flex align-items-center gap-2">
                                        <FaCalendarAlt /> {t('Date')}
                                    </Form.Label>
                                    <Form.Control
                                        type="datetime-local"
                                        value={labTestDate}
                                        onChange={(e) => setLabTestDate(e.target.value)}
                                    />
                                </Form.Group>
                            </>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold d-flex align-items-center gap-2">
                                <FaFileAlt /> {t('Note')}
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={generalNote}
                                onChange={(e) => setGeneralNote(e.target.value)}
                                placeholder={t('Add any additional notes here...')}
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="custom-modal-footer">
                        <Button variant="secondary" onClick={() => setShowModal(false)}>
                            {t('cancel')}
                        </Button>
                        <Button type="submit" variant="primary" disabled={submitting}>
                            {submitting ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    {t('saving')}
                                </>
                            ) : (
                                editingLabTest ? t('update') : t('save')
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* File View Modal */}
            <Modal show={showFileModal} onHide={() => setShowFileModal(false)} size="xl" centered>
                <Modal.Header closeButton className="custom-modal-header">
                    <Modal.Title>{t('View Test File')}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center" style={{ maxHeight: '80vh', overflow: 'auto' }}>
                    {selectedFileUrl && (
                        selectedFileUrl.match(/\.(jpg|jpeg|png|gif|bmp|webp)$/i) ? (
                            <img
                                src={selectedFileUrl}
                                alt="Test File"
                                className="img-fluid"
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '70vh',
                                    objectFit: 'contain'
                                }}
                            />
                        ) : selectedFileUrl.toLowerCase().includes('.pdf') ? (
                            <iframe
                                src={selectedFileUrl}
                                title="Test File"
                                style={{ width: '100%', height: '70vh' }}
                                className="border-0"
                            />
                        ) : (
                            <div className="text-center">
                                <FaFileImage size={80} className="text-muted mb-3" />
                                <p>{t('File preview not available')}</p>
                                <Button
                                    variant="primary"
                                    onClick={() => window.open(selectedFileUrl, '_blank')}
                                >
                                    <FaDownload /> {t('Open File')}
                                </Button>
                            </div>
                        )
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowFileModal(false)}>
                        {t('close')}
                    </Button>
                    <Button variant="primary" onClick={() => window.open(selectedFileUrl, '_blank')}>
                        <FaDownload /> {t('download')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}